import React, { useCallback, useContext, useRef } from 'react';
import {
  arrayOf, bool, func, number, string
} from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { extend, QueryProvider, useDataModel } from '@thd-nucleus/data-sources';
import { Carousel } from '@thd-olt-component-react/carousel';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Image } from '@thd-olt-component-react/image';
import className from 'classnames';
import { dataModel } from './dataModel';
import '../styles/thd-recs-containers.scss';
import { LoadingPlaceholder } from './LoadingPlaceholder';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({}, ProductPod);

const RecommendationsProductPodCarousel = (props) => {
  const { storeId, storeZip: zipCode, isLocalized } = useStore();
  const { channel } = useContext(ExperienceContext);
  const loadedStoreInfo = useRef({ storeId, zipCode });

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    axiosLoading,
    scheme,
    showLoading,
    skus,
    title,

    // carouselProps,
    fixedItem,
    itemClass,
    itemWidthPixelsDesktop,
    itemWidthPixelsMobile,
    multiItem,
    onClickItem,
    showArrows,

    // productPodProps,
    hideATC,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    loadingImageHeight,
    loadingLinesCount,
  } = props;

  const opts = {
    variables: {
      itemIds: skus,
      storeId,
      zipCode,
      dataSource: scheme,
    },
    skip: !isLocalized || skus.length === 0,
    ssr: false
  };

  const { data, variables, loading, error } = useDataModel('recsProducts', opts);

  if (data && !loading && isLocalized) {
    loadedStoreInfo.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true
    };
  }

  // mounted is when you want it to fetch client() only fields
  // such  as fulfillment. You want to use this flag
  // for inner queries to not change to localized values
  // storeId, zip, until the outer query has resolved
  const mountedFn = useCallback(
    () => loadedStoreInfo.current.mounted,
    [loadedStoreInfo.current.mounted]
  );

  const defaultVariables = {
    storeId: loadedStoreInfo.current.storeId,
    zipCode: loadedStoreInfo.current.zipCode
  };

  const wrapperClasses = className(
    'dynamic-recs',
    scheme,
    {
      loading: showLoading && (loading || axiosLoading),
    }
  );

  if (!(data || data?.products?.length > 0) && !loading && !axiosLoading) return null;

  return (
    <Row className={wrapperClasses}>
      <QueryProvider
        dataSource={scheme}
        cacheKey="recs-product-pods"
        mounted={mountedFn}
        defaultVariables={defaultVariables}
      >
        <Wrapper>
          {showLoading && (axiosLoading || loading) && (
            <LoadingPlaceholder
              scheme={scheme}
              itemClass={itemClass}
              fixedItem={fixedItem}
              multiItem={multiItem}
              itemWidthPixelsDesktop={itemWidthPixelsDesktop}
              itemWidthPixelsMobile={itemWidthPixelsMobile}
              loadingLinesCount={loadingLinesCount}
              loadingImageHeight={loadingImageHeight}
            />
          )}
          {data?.products?.length > 0 && (
            <>
              <Col>
                <div className="row-header">
                  <div className="row-header__title">
                    <h2 className="row-header__text">{title}</h2>
                  </div>
                </div>
              </Col>
              {anchorImage && !isMobile && (
                <Col xs="4" className="visually-similar--centered">
                  <Image
                    src={anchorImage}
                    alt="main-product-image"
                    height="400"
                    width="400"
                    data-testid="anchor-image"
                  />
                  <div className="visually-similar__currentItem__label">Current Item</div>
                </Col>
              )}
              <Col xs={anchorImage ? '8' : '12'}>
                <Carousel
                  name={scheme}
                  id={scheme}
                  itemClass={itemClass}
                  fixedItem={fixedItem}
                  multiItem={multiItem}
                  itemWidthPixelsDesktop={itemWidthPixelsDesktop}
                  itemWidthPixelsMobile={itemWidthPixelsMobile}
                  showArrows={showArrows}
                  totalItems={data?.products.length}
                  onClickItem={onClickItem || null}
                >
                  {data?.products.map((product, idx) => {
                    const { itemId } = product;
                    return (
                      <ProductPod
                        key={itemId}
                        itemId={itemId}
                        hideATC={hideATC}
                        hideFavorites={hideFavorites}
                        hideFulfillment={hideFulfillment}
                        hideLimitPerOrder={hideLimitPerOrder}
                        hidePriceBadge={hidePriceBadge}
                        hideRebates={hideRebates}
                        hideSavingsText={hideSavingsText}
                        hideShopThisCollection={hideShopThisCollection}
                        hideShippingInfo={hideShippingInfo}
                        storeId={storeId}
                        position={idx}
                      />
                    );
                  }
                  )}
                </Carousel>
              </Col>
            </>
          )}
        </Wrapper>
      </QueryProvider>
    </Row>
  );
};

RecommendationsProductPodCarousel.displayName = 'RecommendationsProductPodCarousel';

RecommendationsProductPodCarousel.dataModel = dataModel;

RecommendationsProductPodCarousel.propTypes = {
  ...Carousel.propTypes,
  ...ProductPod.propTypes,
  scheme: string,
  skus: arrayOf(string),
  showLoading: bool,
  axiosLoading: bool,
  title: string,

  // carouselProps
  fixedItem: bool,
  multiItem: bool,
  itemClass: string,
  itemWidthPixelsDesktop: number,
  itemWidthPixelsMobile: number,
  onClickItem: func, // requires ( index: number, item: any ),

  // productPodProps
  hideATC: bool,
  hideFavorites: bool,
  hideFulfillment: bool,
  hideLimitPerOrder: bool,
  hidePriceBadge: bool,
  hideRebates: bool,
  hideSavingsText: bool,
  hideShopThisCollection: bool,
  hideShippingInfo: bool,
  loadingImageHeight: number,
  loadingLinesCount: number,
};

RecommendationsProductPodCarousel.defaultProps = {
  scheme: 'drecs',
  skus: [],
  showLoading: false,
  axiosLoading: false,
  title: '',

  fixedItem: true,
  itemClass: '',
  multiItem: false,
  itemWidthPixelsDesktop: 220,
  itemWidthPixelsMobile: 150,
  onClickItem: null,

  hideATC: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  loadingImageHeight: null,
  loadingLinesCount: 5
};

export { RecommendationsProductPodCarousel };
